import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Compass, Heart, MessageSquare, LayoutDashboard, User, ListChecks, Store, Menu, X, Bell, LogOut, FileSearch,
  Users, ShieldCheck, Images, Handshake, Receipt, BarChart3, FileText, ScrollText, ClipboardList, GitCompare, BadgeCheck, Wallet, Tag } from "lucide-react";
import { Logo } from "./Bits";
import { useAuth, hasRole } from "@/lib/auth";
import { useMarket } from "@/lib/market";

const PUBLIC_LINKS = [["/browse", "Explore"], ["/page/how-buying-works", "Buying"], ["/page/how-selling-works", "Selling"], ["/page/verification", "Verification"], ["/page/fees", "Fees"]];

function TopNav() {
  const { user } = useAuth();
  const { compare } = useMarket();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F7F3EB]/85 border-b border-[#E2D6C3] no-print">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
          {PUBLIC_LINKS.map(([to, t]) => (
            <NavLink key={to} to={to} className={({ isActive }) => `px-3 py-2 rounded-full text-sm font-medium ${isActive ? "bg-[#E9DDCB] text-[#29483D]" : "text-[#49392F] hover:bg-[#E9DDCB]/60"}`} data-testid={`nav-${t.toLowerCase()}`}>{t}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {compare.length > 0 && <Link to="/compare" className="btn-ghost btn-sm hidden sm:inline-flex" data-testid="nav-compare"><GitCompare size={14} /> Compare ({compare.length})</Link>}
          {user ? <Link to="/dashboard" className="btn-primary btn-sm" data-testid="nav-dashboard">Dashboard</Link> : (
            <>
              <Link to="/login" className="btn-ghost btn-sm hidden sm:inline-flex" data-testid="nav-login">Log in</Link>
              <Link to="/signup" className="btn-primary btn-sm" data-testid="nav-signup">Sign up</Link>
            </>
          )}
          <button className="lg:hidden btn-ghost btn-sm" onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open} data-testid="mobile-menu-toggle">{open ? <X size={16} /> : <Menu size={16} />}</button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-[#E2D6C3] px-4 py-3 grid gap-1 bg-[#F7F3EB]" onClick={() => setOpen(false)}>
          {[...PUBLIC_LINKS, ["/page/about", "About"], ["/page/help", "Help & contact"], ["/compare", "Compare"], ...(user ? [] : [["/login", "Log in"]])].map(([to, t]) => <Link key={to} to={to} className="py-2.5 px-2 rounded-lg hover:bg-[#E9DDCB]">{t}</Link>)}
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#29483D] text-[#E9DDCB] mt-24 no-print">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2 space-y-3">
          <Logo light />
          <p className="font-display text-xl text-[#F7F3EB] max-w-sm">Someone built it. You can take it forward.</p>
          <p className="text-xs max-w-md opacity-80">VyapaarSetu is a listing and workflow platform. It does not hold purchase funds, provide escrow or value businesses. Figures are supplied by sellers; verification describes only the recorded review scope.</p>
        </div>
        <div className="grid gap-2 text-sm">
          {[["/browse", "Browse businesses"], ["/page/how-buying-works", "How buying works"], ["/page/how-selling-works", "How selling works"], ["/page/verification", "Verification explained"], ["/page/fees", "Fees"]].map(([to, t]) => <Link key={to} to={to} className="hover:text-white">{t}</Link>)}
        </div>
        <div className="grid gap-2 text-sm">
          {[["/page/about", "About"], ["/page/help", "Help and contact"], ["/install", "Install the app"], ["/page/privacy", "Privacy"], ["/page/terms", "Terms"], ["/page/disclosure", "Platform disclosure"]].map(([to, t]) => <Link key={to} to={to} className="hover:text-white">{t}</Link>)}
        </div>
      </div>
      <p className="text-center text-[11px] pb-24 lg:pb-6 opacity-70">© {new Date().getFullYear()} UrbanNexis · vyapaarsetu.urbannexis.com (proposed)</p>
    </footer>
  );
}

const BUYER_TABS = [["/browse", "Explore", Compass], ["/dashboard/saved", "Saved", Heart], ["/dashboard/enquiries", "Enquiries", MessageSquare], ["/dashboard", "Dashboard", LayoutDashboard], ["/dashboard/account", "Account", User]];
const SELLER_TABS = [["/dashboard", "Dashboard", LayoutDashboard], ["/dashboard/listings", "Listings", Store], ["/dashboard/enquiries", "Enquiries", MessageSquare], ["/dashboard/tasks", "Tasks", ListChecks], ["/dashboard/account", "Account", User]];
const REVIEWER_TABS = [["/reviewer", "Cases", FileSearch], ["/dashboard/notifications", "Alerts", Bell], ["/browse", "Explore", Compass], ["/dashboard/account", "Account", User]];
const ADMIN_TABS = [["/admin", "Overview", BarChart3], ["/admin/registrations", "Approvals", BadgeCheck], ["/admin/listings", "Listings", Store], ["/admin/deals", "Deals", Handshake], ["/dashboard/account", "Account", User]];

export function BottomNav() {
  const { user } = useAuth();
  const mode = user?.active_mode;
  const tabs = mode === "seller" ? SELLER_TABS : mode === "reviewer" ? REVIEWER_TABS : mode === "admin" || mode === "super_admin" ? ADMIN_TABS : BUYER_TABS;
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#F7F3EB]/95 backdrop-blur-xl border-t border-[#E2D6C3] no-print" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} aria-label="Mobile">
      <div className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, 1fr)` }}>
        {tabs.map(([to, t, Icon]) => (
          <NavLink key={to} to={to} end={to === "/dashboard" || to === "/admin"} className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium ${isActive ? "text-[#B86545]" : "text-[#49392F]"}`} data-testid={`bottom-nav-${t.toLowerCase()}`}>
            <Icon size={20} />{t}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function PublicLayout() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      {user ? <BottomNav /> : <GuestBottom />}
    </div>
  );
}

function GuestBottom() {
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#F7F3EB]/95 backdrop-blur-xl border-t border-[#E2D6C3] grid grid-cols-4 no-print" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      {[["/browse", "Explore", Compass], ["/compare", "Compare", GitCompare], ["/page/how-selling-works", "Sell", Store], ["/login", "Log in", User]].map(([to, t, Icon]) => (
        <NavLink key={to} to={to} className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2.5 text-[11px] ${isActive ? "text-[#B86545]" : "text-[#49392F]"}`} data-testid={`guest-nav-${t.toLowerCase().replace(" ", "-")}`}><Icon size={20} />{t}</NavLink>
      ))}
    </nav>
  );
}

const SIDEBAR = {
  buyer: [["/dashboard", "Overview", LayoutDashboard], ["/browse", "Explore", Compass], ["/dashboard/saved", "Saved & searches", Heart], ["/compare", "Compare", GitCompare], ["/dashboard/enquiries", "Enquiries & offers", MessageSquare], ["/dashboard/reports", "My activity report", BarChart3], ["/dashboard/registration", "Registration & plan", BadgeCheck], ["/dashboard/notifications", "Notifications", Bell], ["/dashboard/account", "Account", User]],
  seller: [["/dashboard", "Overview", LayoutDashboard], ["/dashboard/listings", "My listings", Store], ["/dashboard/enquiries", "Enquiries & pipeline", MessageSquare], ["/dashboard/tasks", "Verification tasks", ListChecks], ["/dashboard/fees", "Fees", Receipt], ["/dashboard/reports", "Listing reports", BarChart3], ["/dashboard/registration", "Registration & plan", BadgeCheck], ["/dashboard/notifications", "Notifications", Bell], ["/dashboard/account", "Account", User]],
  reviewer: [["/reviewer", "Assigned cases", FileSearch], ["/dashboard/notifications", "Notifications", Bell], ["/dashboard/account", "Account", User]],
  admin: [["/admin", "Overview", BarChart3], ["/admin/registrations", "Registrations queue", BadgeCheck], ["/admin/payments", "Registration payments", Wallet], ["/admin/pricing", "Registration pricing", Tag], ["/admin/users", "Users & roles", Users], ["/admin/listings", "Listings & revisions", Store], ["/admin/verification", "Verification & reviewers", ShieldCheck], ["/admin/moderation", "Image & document moderation", Images], ["/admin/deals", "Enquiries, offers, deals", Handshake], ["/admin/fees", "Fees & billing", Receipt], ["/admin/reports", "Reports", ClipboardList], ["/admin/content", "Content & settings", FileText], ["/admin/audit", "Audit, reports & requests", ScrollText], ["/dashboard/account", "Account", User]],
};

function ModeSwitcher() {
  const { user, setMode } = useAuth();
  const nav = useNavigate();
  const modes = ["buyer", "seller", "reviewer", "admin"].filter((m) => hasRole(user, m));
  if (modes.length < 2) return null;
  const go = async (m) => { await setMode(m); nav(m === "admin" ? "/admin" : m === "reviewer" ? "/reviewer" : "/dashboard"); };
  return (
    <div className="flex flex-wrap gap-1 p-1 rounded-full bg-[#E9DDCB]" role="group" aria-label="Switch role" data-testid="role-switcher">
      {modes.map((m) => (
        <button key={m} onClick={() => go(m)} className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${user.active_mode === m || (m === "admin" && user.active_mode === "super_admin") ? "bg-[#29483D] text-white" : "text-[#49392F]"}`} data-testid={`mode-${m}`}>{m}</button>
      ))}
    </div>
  );
}

export function DashLayout() {
  const { user, logout, unread } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const mode = user?.active_mode === "super_admin" ? "admin" : user?.active_mode || "buyer";
  const items = SIDEBAR[mode] || SIDEBAR.buyer;
  return (
    <div className="min-h-screen lg:flex">
      <aside className="hidden lg:flex flex-col w-72 shrink-0 border-r border-[#E2D6C3] bg-[#F3EDE2] p-5 gap-6 sticky top-0 h-screen overflow-y-auto no-print">
        <Logo />
        <ModeSwitcher />
        <nav className="grid gap-0.5" aria-label="Dashboard">
          {items.map(([to, t, Icon]) => (
            <NavLink key={to} to={to} end={to === "/dashboard" || to === "/admin" || to === "/reviewer"} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm ${isActive ? "bg-[#29483D] text-white" : "text-[#49392F] hover:bg-[#E9DDCB]"}`} data-testid={`side-${to.replace(/\//g, "-").slice(1)}`}>
              <Icon size={17} />{t}{t === "Notifications" && unread > 0 && <span className="ml-auto chip bg-[#B86545] text-white">{unread}</span>}
            </NavLink>
          ))}
        </nav>
        <button className="mt-auto btn-ghost" onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn"><LogOut size={15} /> Log out</button>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-[#F7F3EB]/90 backdrop-blur-xl border-b border-[#E2D6C3] px-4 h-14 flex items-center justify-between no-print">
          <Logo compact />
          <Link to="/dashboard/notifications" className="relative p-2" aria-label="Notifications" data-testid="mobile-notifications"><Bell size={20} />{unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#B86545]" />}</Link>
        </header>
        <div className="lg:hidden px-4 pt-3 no-print"><ModeSwitcher /></div>
        <main className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 pb-28 lg:pb-10 max-w-6xl" key={loc.pathname}><Outlet /></main>
      </div>
      <BottomNav />
    </div>
  );
}

export const PageHead = ({ eyebrow, title, sub, right }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 fade-up">
    <div>{eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}<h1 className="text-3xl sm:text-4xl text-[#29483D]">{title}</h1>{sub && <p className="text-sm text-stone-600 mt-2 max-w-2xl">{sub}</p>}</div>
    {right}
  </div>
);
